import type {ReactNode} from 'react'
import {cn} from '@/lib/utils'

type DetailListGap = 'sm' | 'md' | 'lg'
type DetailListColumns = 1 | 2 | 3 | 4
type DetailListBreakpoint = 'sm' | 'md'

interface DetailListProps {
    children: ReactNode
    /** Vertical spacing between rows when in stacked (default) layout. */
    gap?: DetailListGap
    /**
     * When set to a number, the list is rendered as a responsive grid with that many
     * columns at `lg` (scales down based on `breakpoint`). When omitted the list is a
     * vertical stack with `gap` spacing.
     */
    columns?: DetailListColumns
    /** Breakpoint at which the grid switches from 1 to 2 columns. Defaults to `md`. */
    breakpoint?: DetailListBreakpoint
    /**
     * Extra classes appended to the rendered `<dl>`. Use to override the default
     * `gap-4` for grid layouts (e.g. `gap-x-6 gap-y-3`).
     */
    className?: string
}

/* eslint-disable i18next/no-literal-string -- CSS class constants */
const stackGapClasses: Record<DetailListGap, string> = {
    sm: 'space-y-2',
    md: 'space-y-4',
    lg: 'space-y-6',
}

const gridColsByBreakpoint: Record<DetailListBreakpoint, Record<DetailListColumns, string>> = {
    md: {
        1: 'grid grid-cols-1 gap-4',
        2: 'grid grid-cols-1 md:grid-cols-2 gap-4',
        3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
        4: 'grid grid-cols-2 md:grid-cols-4 gap-4',
    },
    sm: {
        1: 'grid grid-cols-1 gap-4',
        2: 'grid grid-cols-1 sm:grid-cols-2 gap-4',
        3: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
        4: 'grid grid-cols-2 sm:grid-cols-4 gap-4',
    },
}
/* eslint-enable i18next/no-literal-string */

export function DetailList({
    children,
    gap = 'md',
    columns,
    breakpoint = 'md',
    className,
}: DetailListProps) {
    const layoutClass = columns !== undefined
        ? gridColsByBreakpoint[breakpoint][columns]
        : stackGapClasses[gap]
    return (
        <dl className={cn(layoutClass, 'text-sm', className)}>
            {children}
        </dl>
    )
}
